// Training Center → Recommendations tab.
//
// Replaces web/scripts/training-center.js's tcLoadRecommendations()
// and _tcRenderRecommendations(). Card list of pending recommendations
// with Apply / Dismiss buttons + an Analyze button that kicks off a
// new analysis run. Runs history appears below.

import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnalyzeRun, OrgScope, Recommendation, RetypeTarget, TestSqlResult } from "../../api/training";
import { RETYPE_TARGETS, trainingApi } from "../../api/training";
import { AdminGrid, ErrorBlock, LoadingBlock, formatDateTime, formatRelative } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function RecommendationsTab({ bearer, scope }: Props) {
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [runs, setRuns] = useState<AnalyzeRun[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [busyRec, setBusyRec] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      trainingApi
        // No status filter → backend returns actionable recs (status IN 'ready','tested').
        .recommendations(bearer, scope)
        .catch(() => [] as Recommendation[]),
      trainingApi
        .recommendationRuns(bearer, scope)
        
        .catch(() => [] as AnalyzeRun[]),
    ])
      .then(([r, runRows]) => {
        if (cancelled) return;
        setRecs(r);
        setRuns(runRows);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  useEffect(() => {
    setLoading(true);
    const c = reload();
    setLoading(false);
    return c;
  }, [reload]);

  const startAnalyze = async () => {
    if (!scope.organization) {
      setError(new Error("Select an organization to analyze."));
      return;
    }
    setAnalyzing(true);
    setError(null);
    try {
      await trainingApi.analyze(bearer, scope);
    } catch (e) {
      setError(e as Error);
    } finally {
      // Poll for completion: refresh runs every 4s for up to 2 min.
      let attempts = 0;
      const tick = async () => {
        attempts++;
        const c = await trainingApi
          .recommendationRuns(bearer, scope)
          
          .catch(() => null);
        if (c) {
          setRuns(c);
          const latest = c[0];
          if (!latest || latest.status !== "running") {
            setAnalyzing(false);
            reload();
            return;
          }
        }
        if (attempts < 30) setTimeout(tick, 4000);
        else setAnalyzing(false);
      };
      setTimeout(tick, 4000);
    }
  };

  const handleApply = async (rec: Recommendation) => {
    setBusyRec(rec.id);
    try {
      await trainingApi.applyRecommendation(bearer, rec.id);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyRec(null);
    }
  };

  const handleRetype = async (rec: Recommendation, newType: RetypeTarget) => {
    setBusyRec(rec.id);
    setError(null);
    try {
      const updated = await trainingApi.retypeRecommendation(bearer, rec.id, newType);
      // Replace the row in place so the card reflects the regenerated proposal.
      setRecs((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyRec(null);
    }
  };

  const handleDismiss = async (rec: Recommendation) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt("Dismiss reason (optional):") ?? undefined;
    setBusyRec(rec.id);
    try {
      await trainingApi.dismissRecommendation(bearer, rec.id, reason);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyRec(null);
    }
  };

  const handleEdit = async (
    rec: Recommendation,
    body: { proposed_change?: Record<string, unknown>; title?: string; summary?: string },
  ): Promise<boolean> => {
    setBusyRec(rec.id);
    setError(null);
    try {
      const updated = await trainingApi.editRecommendation(bearer, rec.id, body);
      setRecs((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      return true;
    } catch (e) {
      setError(e as Error);
      return false;
    } finally {
      setBusyRec(null);
    }
  };

  const handleTest = (rec: Recommendation, sql: string) => {
    const pc = (rec.proposed_change ?? {}) as Record<string, unknown>;
    const tables = Array.isArray(pc.tables) ? (pc.tables as unknown[]) : [];
    const datasource =
      (typeof pc.datasource === "string" && pc.datasource) ||
      (typeof tables[0] === "string" ? (tables[0] as string).split(".")[0] : "") ||
      "";
    return trainingApi.testSql(bearer, { organization: scope.organization, datasource, sql });
  };

  const runColDefs = useMemo<ColDef<AnalyzeRun>[]>(
    () => [
      {
        field: "started_at",
        headerName: "Started",
        flex: 1,
        sort: "desc",
        valueFormatter: (p) => (p.value ? formatDateTime(p.value as string) : "—"),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        cellRenderer: (p: { value: AnalyzeRun["status"] }) => <RunStatusBadge status={p.value} />,
      },
      {
        field: "recommendations_ready",
        headerName: "Recs",
        width: 90,
        type: "numericColumn",
        valueFormatter: (p) => (p.value ?? "—") as string,
      },
      {
        field: "completed_at",
        headerName: "Finished",
        flex: 1,
        valueFormatter: (p) => (p.value ? formatRelative(p.value as string) : "—"),
      },
      {
        field: "error_message",
        headerName: "Error",
        flex: 2,
        autoHeight: true,
        wrapText: true,
        cellClass: "text-red-700 text-xs",
        valueFormatter: (p) => (p.value as string) || "",
      },
    ],
    [],
  );

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-900">AI Recommendations</h2>
        <button
          type="button"
          onClick={startAnalyze}
          disabled={analyzing}
          className="rounded-md bg-brand text-white px-3 h-9 text-sm font-medium hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {analyzing ? "Analyzing…" : "Analyze"}
        </button>
      </div>

      {error && <ErrorBlock error={error} />}

      {recs.length === 0 ? (
        <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
          Click "Analyze" to generate AI recommendations for improving query quality.
        </div>
      ) : (
        <div className="space-y-3">
          {recs.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              busy={busyRec === rec.id}
              onApply={() => handleApply(rec)}
              onDismiss={() => handleDismiss(rec)}
              onRetype={(newType) => handleRetype(rec, newType)}
              onSave={(body) => handleEdit(rec, body)}
              onTest={(sql) => handleTest(rec, sql)}
            />
          ))}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">Analysis Runs</h3>
        {runs.length === 0 ? (
          <div className="text-sm text-neutral-500">No runs yet.</div>
        ) : (
          <AdminGrid<AnalyzeRun> rowData={runs.slice(0, 15)} columnDefs={runColDefs} />
        )}
      </div>
    </div>
  );
}

function RecommendationCard({
  rec,
  busy,
  onApply,
  onDismiss,
  onRetype,
  onSave,
  onTest,
}: {
  rec: Recommendation;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
  onRetype: (newType: RetypeTarget) => void;
  onSave: (body: { proposed_change?: Record<string, unknown>; title?: string; summary?: string }) => Promise<boolean>;
  onTest: (sql: string) => Promise<TestSqlResult>;
}) {
  const [editing, setEditing] = useState(false);
  // auto_promote packages an already-correct query — it has no failure cluster
  // to regenerate from, so it keeps a static badge instead of the retype dropdown.
  const canRetype = rec.type !== "auto_promote";
  const edited = Boolean((rec.proposed_change as Record<string, unknown> | null)?._edited);
  return (
    <div className="rounded-[14px] border border-line bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {canRetype ? (
              <label className="flex items-center gap-1.5">
                <span className="text-[11px] text-neutral-500">Type</span>
                <select
                  value={(RETYPE_TARGETS as readonly string[]).includes(rec.type) ? rec.type : ""}
                  onChange={(e) => {
                    const next = e.target.value as RetypeTarget;
                    if (next && next !== rec.type) onRetype(next);
                  }}
                  disabled={busy || editing}
                  title="Change recommendation type — regenerates the proposed change"
                  className="h-7 px-2 pr-6 text-[12px] rounded-md border border-line bg-white text-neutral-800 hover:bg-chip focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {!(RETYPE_TARGETS as readonly string[]).includes(rec.type) && (
                    <option value="" disabled>
                      {rec.type}
                    </option>
                  )}
                  {RETYPE_TARGETS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className="inline-block rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium px-2 py-0.5">
                {rec.type}
              </span>
            )}
            {edited && (
              <span
                className="inline-block rounded-full bg-purple-50 text-purple-700 text-[11px] font-medium px-2 py-0.5"
                title="This proposal was edited by an admin"
              >
                edited
              </span>
            )}
            {busy && <span className="text-[11px] text-neutral-400">working…</span>}
            {rec.net_impact_score != null && Number.isFinite(Number(rec.net_impact_score)) && (
              <span className="text-[11px] text-neutral-500">
                impact {Number(rec.net_impact_score).toFixed(2)}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-semibold text-neutral-900">{rec.title}</div>
          {rec.summary && <div className="mt-1 text-sm text-neutral-700">{rec.summary}</div>}
          {editing ? (
            <EditProposalForm
              rec={rec}
              busy={busy}
              onTest={onTest}
              onCancel={() => setEditing(false)}
              onSave={async (body) => {
                const ok = await onSave(body);
                if (ok) setEditing(false);
              }}
            />
          ) : (
            rec.proposed_change && <ProposedChangeView change={rec.proposed_change} type={rec.type} />
          )}
          {rec.root_cause && !editing && (
            <details className="mt-2 text-xs text-neutral-600">
              <summary className="cursor-pointer hover:text-neutral-900">Why?</summary>
              <div className="mt-1 whitespace-pre-wrap">{rec.root_cause}</div>
            </details>
          )}
        </div>
        {!editing && (
          <div className="flex flex-col gap-2 shrink-0">
            <button
              type="button"
              onClick={onApply}
              disabled={busy}
              className="rounded-md bg-emerald-600 text-white px-3 h-8 text-xs font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="rounded-md border border-line text-neutral-800 px-3 h-8 text-xs font-medium hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="rounded-md border border-line text-neutral-800 px-3 h-8 text-xs font-medium hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Editable fields per recommendation type — keys mirror the proposed_change shape
// (training/recommender.py proposal models). SQL fields get a Test button.
type EditFieldKind = "text" | "textarea" | "list" | "bool";
interface EditField {
  key: string;
  label: string;
  kind: EditFieldKind;
  sql?: boolean;
}
// Focused set: only the fields where a hand edit is high-value and lower-risk.
// Retrieval-routing (tables/conditional_tables), identifiers (metric_name, table,
// column, entity_name) and derived/cosmetic fields are intentionally omitted —
// retype/regenerate those instead of editing in place.
const _PROMPT_FIELDS: EditField[] = [
  { key: "user_query", label: "Query", kind: "text" },
  { key: "corrected_sql", label: "SQL", kind: "textarea", sql: true },
  { key: "always_include", label: "Always include", kind: "bool" },
];
const EDIT_FIELDS: Record<string, EditField[]> = {
  prompt_example: _PROMPT_FIELDS,
  auto_promote: _PROMPT_FIELDS,
  measure: [{ key: "formula", label: "Formula (bare SQL expression)", kind: "textarea" }],
  column_rule: [
    { key: "enhanced_description", label: "Description", kind: "textarea" },
    { key: "sql_rule", label: "SQL rule", kind: "textarea" },
  ],
  relationship: [
    { key: "source_table", label: "Source table (FK / many side)", kind: "text" },
    { key: "source_column", label: "Source column", kind: "text" },
    { key: "target_table", label: "Target table (referenced / one side)", kind: "text" },
    { key: "target_column", label: "Target column", kind: "text" },
    { key: "join_type", label: "Join type (LEFT / INNER / FULL)", kind: "text" },
  ],
  entity: [{ key: "maps_to_column", label: "Maps to column (schema.table.column)", kind: "text" }],
};

function EditProposalForm({
  rec,
  busy,
  onTest,
  onSave,
  onCancel,
}: {
  rec: Recommendation;
  busy: boolean;
  onTest: (sql: string) => Promise<TestSqlResult>;
  onSave: (body: { proposed_change?: Record<string, unknown>; title?: string; summary?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const fields = EDIT_FIELDS[rec.type] ?? [];
  const pc = (rec.proposed_change ?? {}) as Record<string, unknown>;

  const initial = useMemo(() => {
    const m: Record<string, string | boolean> = {};
    for (const f of fields) {
      const v = pc[f.key];
      if (f.kind === "bool") m[f.key] = Boolean(v);
      else if (f.kind === "list") m[f.key] = Array.isArray(v) ? v.join(", ") : "";
      else m[f.key] = typeof v === "string" ? v : "";
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.id, rec.type]);

  const [form, setForm] = useState<Record<string, string | boolean>>(initial);
  const [title, setTitle] = useState(rec.title ?? "");
  const [testState, setTestState] = useState<"idle" | "running" | "ok" | "err">("idle");
  const [testMsg, setTestMsg] = useState<string>("");

  const setField = (k: string, v: string | boolean) => setForm((p) => ({ ...p, [k]: v }));

  const buildBody = () => {
    const proposed_change: Record<string, unknown> = {};
    for (const f of fields) {
      const v = form[f.key];
      if (f.kind === "bool") proposed_change[f.key] = Boolean(v);
      else if (f.kind === "list")
        proposed_change[f.key] = String(v)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      else proposed_change[f.key] = String(v);
    }
    return { proposed_change, title };
  };

  const runTest = async (sql: string) => {
    setTestState("running");
    setTestMsg("");
    try {
      const r = await onTest(sql);
      setTestState("ok");
      setTestMsg(`OK · ${r.row_count} row(s) · ${r.execution_time_ms} ms`);
    } catch (e) {
      setTestState("err");
      setTestMsg((e as Error).message || "SQL failed");
    }
  };

  return (
    <div className="mt-3 space-y-2.5 rounded-md border border-line bg-chip/40 p-3">
      <label className="block">
        <span className="text-[11px] font-medium text-neutral-500">Title</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-0.5 w-full h-8 px-2 text-sm rounded-md border border-line bg-white focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </label>
      {fields.map((f) => (
        <div key={f.key}>
          {f.kind === "bool" ? (
            <label className="flex items-center gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={Boolean(form[f.key])}
                onChange={(e) => setField(f.key, e.target.checked)}
              />
              {f.label}
            </label>
          ) : (
            <label className="block">
              <span className="text-[11px] font-medium text-neutral-500">{f.label}</span>
              {f.kind === "textarea" ? (
                <textarea
                  value={String(form[f.key] ?? "")}
                  onChange={(e) => setField(f.key, e.target.value)}
                  rows={f.sql ? 5 : 3}
                  className={`mt-0.5 w-full px-2 py-1 text-[12px] rounded-md border border-line bg-white focus:outline-none focus:ring-1 focus:ring-brand ${
                    f.sql ? "font-mono" : ""
                  }`}
                />
              ) : (
                <input
                  value={String(form[f.key] ?? "")}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="mt-0.5 w-full h-8 px-2 text-[12px] rounded-md border border-line bg-white focus:outline-none focus:ring-1 focus:ring-brand font-mono"
                />
              )}
              {f.sql && (
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => runTest(String(form[f.key] ?? ""))}
                    disabled={testState === "running" || !String(form[f.key] ?? "").trim()}
                    className="rounded-md border border-line px-2 h-7 text-[11px] hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {testState === "running" ? "Testing…" : "Test SQL"}
                  </button>
                  {testMsg && (
                    <span
                      className={`text-[11px] ${
                        testState === "ok" ? "text-emerald-700" : testState === "err" ? "text-red-700" : "text-neutral-500"
                      }`}
                    >
                      {testMsg}
                    </span>
                  )}
                </div>
              )}
            </label>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave(buildBody())}
          disabled={busy}
          className="rounded-md bg-brand text-white px-3 h-8 text-xs font-medium hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-line text-neutral-800 px-3 h-8 text-xs font-medium hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Renders the exact change a recommendation will apply, so the admin can review
// it (e.g. the corrected SQL) before clicking Apply. Shape varies by rec type —
// see training/recommender.py proposal models.
function ProposedChangeView({ change, type }: { change: Record<string, unknown>; type: string }) {
  const str = (k: string): string | null => {
    const v = change[k];
    return typeof v === "string" && v.trim() ? v : null;
  };
  const list = (k: string): string[] => {
    const v = change[k];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  };

  // prompt_example / auto_promote → show the corrected SQL prominently.
  const correctedSql = str("corrected_sql");
  if (type === "prompt_example" || type === "auto_promote" || correctedSql) {
    const userQuery = str("user_query");
    const tables = list("tables");
    return (
      <div className="mt-2 space-y-1.5">
        {userQuery && (
          <div className="text-xs text-neutral-600">
            <span className="font-medium text-neutral-500">Query:</span> {userQuery}
          </div>
        )}
        {correctedSql && (
          <pre className="overflow-x-auto rounded-md bg-neutral-900 text-neutral-100 text-[11px] leading-relaxed p-2.5 whitespace-pre-wrap">
            {correctedSql}
          </pre>
        )}
        {tables.length > 0 && (
          <div className="text-[11px] text-neutral-500">Tables: {tables.join(", ")}</div>
        )}
      </div>
    );
  }

  // Semantic-model recs → small key/value list of the meaningful fields.
  const rows: [string, string][] = [];
  const push = (label: string, k: string) => {
    const v = str(k);
    if (v) rows.push([label, v]);
  };
  push("Entity", "entity_name");
  push("Maps to", "maps_to_column");
  push("Join", "source_table");
  push("→", "target_table");
  push("Metric", "metric_name");
  push("Aggregation", "aggregation");
  push("Formula", "formula");
  push("Column", "column");
  push("Description", "enhanced_description");
  push("SQL rule", "sql_rule");
  if (rows.length === 0) return null;
  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-neutral-700">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-medium text-neutral-500">{label}</dt>
          <dd className="min-w-0 break-words font-mono text-[11px]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function RunStatusBadge({ status }: { status: AnalyzeRun["status"] }) {
  const cls =
    status === "complete"
      ? "bg-emerald-50 text-emerald-700"
      : status === "running"
        ? "bg-blue-50 text-blue-700"
        : "bg-red-50 text-red-700";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {status}
    </span>
  );
}
